/**
 * The DICOM files of one slide, picked together, become ONE image.
 *
 * A scanner exports a whole-slide image as one .dcm per pyramid level, plus its
 * label and overview, all of one series. Sent as one image per file, the files
 * raced for the slide: the first took it and every other was refused as
 * "already catalogued" (2026-09-23). Here the files a person picks are grouped
 * by the SeriesInstanceUID in each file's header: the several .dcm files of one
 * series are one slide, which the upload page packs into one tar (tar.ts) and
 * sends as one file, so the slide is one upload, one conversion, one outcome,
 * and is published only whole.
 *
 * Anything else is left as it was picked: another format, a single .dcm, a
 * .dcm whose header cannot be read. A .dcm whose header says it is an X-ray is
 * remembered as one (isXray): its Type is certain.
 */

import { RADIOGRAPH_SOP_CLASSES } from '../../data/imageTypes';
import { folderOf } from './fileHandles';

/** What a .dcm's header says it is part of. */
export interface DicomIdentity {
  series: string;
  sop: string | null;
  /** Its SOP Class UID (0008,0016), when the header says it. */
  sopClass?: string | null;
}

/** The picked files whose headers say they are X-rays (slidesOf reads them). */
const xrays = new WeakSet<File>();

/**
 * Whether a picked .dcm's header said it is an X-ray: certainly Radiography,
 * whatever it is stored as. Known once slidesOf has read it.
 */
export const isXray = (file: File): boolean => xrays.has(file);

// ── the header ──────────────────────────────────────────────────────────────

/** How far into a file its identity is looked for: it precedes the pixel data. */
const HEAD_STEPS = [256 * 1024, 2 * 1024 * 1024, 16 * 1024 * 1024];

const IMPLICIT_LE = '1.2.840.10008.1.2';
const EXPLICIT_BE = '1.2.840.10008.1.2.2';
const DEFLATED = '1.2.840.10008.1.2.1.99';
/** Explicit VRs whose length is 32 bits, after two reserved bytes. */
const LONG_VRS = new Set(['OB', 'OD', 'OF', 'OL', 'OV', 'OW', 'SQ', 'SV', 'UC', 'UN', 'UR', 'UT', 'UV']);
/** Every VR, to tell explicit from implicit encoding by what the first element holds. */
const VRS = new Set(['AE', 'AS', 'AT', 'CS', 'DA', 'DS', 'DT', 'FD', 'FL', 'IS', 'LO', 'LT', 'OB', 'OD', 'OF', 'OL', 'OV', 'OW', 'PN', 'SH', 'SL', 'SQ', 'SS', 'ST', 'SV', 'TM', 'UC', 'UI', 'UL', 'UN', 'UR', 'US', 'UT', 'UV']);
const UNDEFINED = 0xffffffff;
const ITEM = 0xfffee000;
const ITEM_END = 0xfffee00d;
const SEQUENCE_END = 0xfffee0dd;

/** The bytes ran out before the answer: read further into the file. */
class NeedMore extends Error {}

class Reader {
  pos = 0;
  constructor(private readonly view: DataView, public little: boolean) {}
  need(n: number) {
    if (this.pos + n > this.view.byteLength) throw new NeedMore();
  }
  u16() {
    this.need(2);
    const v = this.view.getUint16(this.pos, this.little);
    this.pos += 2;
    return v;
  }
  u32() {
    this.need(4);
    const v = this.view.getUint32(this.pos, this.little);
    this.pos += 4;
    return v;
  }
  tag() {
    const group = this.u16();
    return ((group << 16) | this.u16()) >>> 0;
  }
  vr() {
    this.need(2);
    const v = String.fromCharCode(this.view.getUint8(this.pos), this.view.getUint8(this.pos + 1));
    this.pos += 2;
    return v;
  }
  text(n: number) {
    this.need(n);
    let s = '';
    for (let i = 0; i < n; i++) s += String.fromCharCode(this.view.getUint8(this.pos + i));
    this.pos += n;
    // UIDs are padded with NUL, other strings with spaces.
    return s.replace(/[\0 ]+$/, '');
  }
  skip(n: number) {
    this.need(n);
    this.pos += n;
  }
}

interface Element {
  tag: number;
  vr: string | null;
  length: number;
}

function element(r: Reader, explicit: boolean): Element {
  const tag = r.tag();
  // Items and delimiters carry no VR in any transfer syntax.
  if (tag === ITEM || tag === ITEM_END || tag === SEQUENCE_END) return { tag, vr: null, length: r.u32() };
  if (!explicit) return { tag, vr: null, length: r.u32() };
  const vr = r.vr();
  if (LONG_VRS.has(vr)) {
    r.skip(2);
    return { tag, vr, length: r.u32() };
  }
  return { tag, vr, length: r.u16() };
}

/** Pass over a sequence of undefined length: its items, and theirs, to its end. */
function skipSequence(r: Reader, explicit: boolean): void {
  for (;;) {
    const item = element(r, explicit);
    if (item.tag === SEQUENCE_END) return;
    if (item.tag !== ITEM) throw new Error('not a sequence item');
    if (item.length !== UNDEFINED) {
      r.skip(item.length);
      continue;
    }
    // An item of undefined length: its elements, to its end.
    for (;;) {
      const e = element(r, explicit);
      if (e.tag === ITEM_END) break;
      skipValue(r, e, explicit);
    }
  }
}

function skipValue(r: Reader, e: Element, explicit: boolean): void {
  if (e.length !== UNDEFINED) {
    r.skip(e.length);
    return;
  }
  // Undefined length is a sequence (or UN holding one, encoded implicit little
  // endian); encapsulated pixel data comes long after the identity.
  skipSequence(r, e.vr === 'UN' ? false : explicit);
}

/**
 * The series and instance a Part 10 file's header names, or null when it has
 * no Part 10 header, cannot be read here (deflated), or names no series.
 * Throws NeedMore when `bytes` end before the answer.
 */
export function identityOf(bytes: ArrayBuffer): DicomIdentity | null {
  const view = new DataView(bytes);
  if (view.byteLength < 132) throw new NeedMore();
  if (String.fromCharCode(view.getUint8(128), view.getUint8(129), view.getUint8(130), view.getUint8(131)) !== 'DICM') return null;
  // The file meta group: always explicit VR little endian.
  const meta = new Reader(view, true);
  meta.pos = 132;
  let syntax = '';
  for (;;) {
    const start = meta.pos;
    meta.need(2);
    if (view.getUint16(meta.pos, true) !== 0x0002) {
      meta.pos = start;
      break;
    }
    const e = element(meta, true);
    if (e.length === UNDEFINED) return null;
    if (e.tag === 0x00020010) syntax = meta.text(e.length);
    else meta.skip(e.length);
  }
  if (syntax === DEFLATED) return null;
  // Some exporters declare one VR encoding and write the other; the first
  // element tells which it is, as pydicom reads it (a VR where one would be).
  let explicit = syntax !== IMPLICIT_LE;
  if (meta.pos + 6 <= view.byteLength) {
    explicit = VRS.has(String.fromCharCode(view.getUint8(meta.pos + 4), view.getUint8(meta.pos + 5)));
  }
  const r = new Reader(view, syntax !== EXPLICIT_BE);
  r.pos = meta.pos;
  let sop: string | null = null;
  let sopClass: string | null = null;
  for (;;) {
    const e = element(r, explicit);
    if (e.tag > 0x0020000e) return null; // past where the series is: it has none
    if (e.tag === 0x00080016 && e.length !== UNDEFINED) sopClass = r.text(e.length) || null;
    else if (e.tag === 0x00080018 && e.length !== UNDEFINED) sop = r.text(e.length) || null;
    else if (e.tag === 0x0020000e && e.length !== UNDEFINED) {
      const series = r.text(e.length);
      return series ? { series, sop, ...(sopClass ? { sopClass } : {}) } : null;
    } else skipValue(r, e, explicit);
  }
}

/** A Blob's bytes; FileReader where a Blob cannot say them itself (jsdom). */
export function bytesOf(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.onerror = () => reject(r.error ?? new Error('could not read the file'));
    r.readAsArrayBuffer(blob);
  });
}

/** A .dcm's identity, read from the start of the file; null when it has none to read. */
export async function readDicomIdentity(file: Blob): Promise<DicomIdentity | null> {
  for (const step of HEAD_STEPS) {
    const n = Math.min(file.size, step);
    try {
      return identityOf(await bytesOf(file.slice(0, n)));
    } catch (err) {
      if (!(err instanceof NeedMore) || n === file.size) return null;
    }
  }
  return null;
}

/** How many files' headers are read at once: a folder of a thousand levels is read a few at a time. */
const HEADER_READS = 6;

/** Each file's identity, read a few at a time, in the files' order. */
async function identitiesOf(files: readonly File[]): Promise<Array<DicomIdentity | null>> {
  const out: Array<DicomIdentity | null> = new Array(files.length).fill(null);
  let next = 0;
  const worker = async () => {
    while (next < files.length) {
      const i = next++;
      out[i] = isDcm(files[i]) ? await readDicomIdentity(files[i]).catch(() => null) : null;
    }
  };
  await Promise.all(Array.from({ length: Math.min(HEADER_READS, files.length) }, worker));
  return out;
}

// ── the slide ───────────────────────────────────────────────────────────────

/** The several files of one slide, picked together: one image, sent as one tar (tar.ts). */
export interface PickedSlide {
  /** What the slide is called: the folder it came in, else its base level's name (slideName); a MIRAX's .mrxs stem. */
  name: string;
  /** What the files are: the DICOM files of one series, or a MIRAX .mrxs with its data folder (mirax.ts). */
  holds: 'dcm' | 'mrxs';
  /** Its files, in name order. */
  files: File[];
  /** The name each file goes by in the tar, when not its own: a MIRAX keeps its folder (`Slide/Data0000.dat`). */
  paths?: string[];
}

/** What a person picked, as the images it makes: a file, or the files of one slide. */
export type Picked = File | PickedSlide;

export const isSlide = (p: Picked): p is PickedSlide => !(p instanceof File);

const isDcm = (f: File) => f.name.toLowerCase().endsWith('.dcm');
const stemOf = (name: string) => name.replace(/\.[^.]*$/, '');

/** A name the server takes (uploads.ts validUploadName): no separators, dot runs or control characters. */
function safeName(name: string): string {
  return name.replace(/[\x00-\x1f\x7f\\/]/g, '_').replace(/\.{2,}/g, '.').replace(/^[.\s]+/, '').slice(0, 200).trim();
}

/** The folder all of these files sit in, or null when they sit in none or in several. */
function sharedFolder(files: readonly File[]): string | null {
  const folders = new Set(files.map((f) => folderOf(f)));
  const [folder] = folders;
  return folders.size === 1 && folder ? folder : null;
}

/**
 * What a slide is called: the folder its files sit in, when they sit in one
 * (a scanner's export is a folder per slide); with its base level's name when
 * that folder holds other slides too (`crowded`); else its base level's name,
 * the largest of its files, without the extension.
 */
export function slideName(files: readonly File[], crowded = false): string {
  const base = [...files].sort((a, b) => b.size - a.size || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))[0];
  const stem = safeName(stemOf(base?.name ?? '')) || 'slide';
  const folder = sharedFolder(files);
  if (!folder || !safeName(folder)) return stem;
  return crowded ? safeName(`${folder} (${stem})`) : safeName(folder);
}

/**
 * The files a person picked, as the images they make: the .dcm files of one
 * series become one slide, in the place its first file had; everything else is
 * as picked. The same instance picked twice (a copy) goes in once.
 *
 * Every .dcm's header is read, a single one's too: it says whether the file
 * is an X-ray (isXray).
 */
export async function slidesOf(files: readonly File[]): Promise<Picked[]> {
  if (!files.some(isDcm)) return [...files];
  const ids = await identitiesOf(files);
  ids.forEach((id, i) => {
    if (id?.sopClass && RADIOGRAPH_SOP_CLASSES.includes(id.sopClass)) xrays.add(files[i]);
  });
  const bySeries = new Map<string, File[]>();
  const sops = new Map<string, Set<string>>();
  ids.forEach((id, i) => {
    if (!id) return;
    const group = bySeries.get(id.series) ?? [];
    const seen = sops.get(id.series) ?? new Set<string>();
    // A copy of a file already in the group: the slide has it.
    if (id.sop && seen.has(id.sop)) return;
    if (id.sop) seen.add(id.sop);
    group.push(files[i]);
    bySeries.set(id.series, group);
    sops.set(id.series, seen);
  });
  // How many slides each folder holds: one is named for its folder, several for their files too.
  const slidesIn = new Map<string, number>();
  for (const group of bySeries.values()) {
    const folder = group.length >= 2 ? sharedFolder(group) : null;
    if (folder) slidesIn.set(folder, (slidesIn.get(folder) ?? 0) + 1);
  }
  const out: Picked[] = [];
  const placed = new Set<string>();
  files.forEach((f, i) => {
    const id = ids[i];
    const group = id ? bySeries.get(id.series) : undefined;
    if (!id || !group) return void out.push(f);
    if (group.length < 2) {
      if (group[0] === f) out.push(f);
      return;
    }
    if (placed.has(id.series)) return;
    placed.add(id.series);
    const inOrder = [...group].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : a.size - b.size));
    const folder = sharedFolder(inOrder);
    out.push({ name: slideName(inOrder, !!folder && (slidesIn.get(folder) ?? 0) > 1), holds: 'dcm', files: inOrder });
  });
  return out;
}
